import { Box } from '@mui/material';
import {
  AskInstructionModel,
  BulletedListType,
  ComprehensionPassage,
  ContentElementType,
  ExcerptType,
  HeadingType,
  ImageOption,
  ImageType,
  InstructionElementType,
  McqMultipleContentModel,
  McqSingleContentModel,
  NumberedListType,
  Question,
  QuestionContentModel,
  ResourceCategoryEnum,
  SubHeadingType,
  TableType,
  TextBoxType,
  TextCenterType,
  TextLeftType,
  TextOption,
  TextRightType,
  TextType,
} from '@protos/content_management/content.db_pb';
import React, { CSSProperties } from 'react';
import {
  getMediaBasePath,
  shouldIncludeCredentialsInRequest,
} from '../../../../commonUtils/images';
import { ElementHeadingTag } from '../../../composites/ElementRenderer/ElementHeadingTag';
import { ImagePopup } from '../../../composites/ElementRenderer/ImagePopup';
import { TableElement } from '../../../composites/ElementRenderer/Table';
import ImageWrapper, { isGifMp4 } from '../../../elements/ImageWrapper';
import { GeneoKatex } from '../GeneoKatex';
import {
  BlankCounter,
  getSolutionText,
  numberToColumnTitle,
} from '../question-utils';

const printableStyles: CSSProperties = {
  breakInside: 'avoid', // For modern browsers and html2pdf.js
  pageBreakInside: 'avoid', // For legacy support
  // WebkitColumnBreakInside: 'avoid', // For Chrome/Safari
  // MozPageBreakInside: 'avoid', // For Firefox
};

const styles = {
  textBoxWrapper: {
    // Add any required styles here or import from QuestionsList if needed
  },
  textElementsWrapper: {
    // Add any required styles here or import from QuestionsList if needed
  },
  textInput: {
    minWidth: '70px',
    margin: '0 4px',
    borderBottom: '1px solid #000',
    fontSize: 'inherit',
    display: 'inline-block',
  },
};

const renderElements = (
  elements?: ContentElementType[],
  isFibText?: boolean,
  resourceCategory?: ResourceCategoryEnum,
  showMedia: boolean = true,
  imageResizeEnabled?: boolean,
  // When true, text is allowed to break across pages (PDF) instead of being
  // kept together. Images stay non-breakable.
  splittable?: boolean
) => {
  const textElements = (
    texts: string[],
    sx: CSSProperties | undefined,
    variant: any,
    component?: any
  ): JSX.Element[] => {
    return texts.map((text, index) => {
      return (
        <Box component={component} key={index} style={sx}>
          <PrintableText
            component={component === 'span' ? 'span' : 'div'}
            style={typeof variant === 'object' ? variant : {}}
          >
            {text}
          </PrintableText>
        </Box>
      );
    });
  };
  const splitText = (t: string): string[] =>
    t.split('<br>').filter((text) => text);

  const blankCounter = new BlankCounter();

  const renderFibText = (
    qParts: string[],
    style: CSSProperties | undefined,
    variant: any,
    wrapperComponent?: any
  ) => {
    return (
      <Box
        component={wrapperComponent}
        style={printableStyles}
        className={wrapperComponent === 'li' ? undefined : 'avoid-break-element'}
      >
        {qParts.map((part, index) => {
          if (index === 0) {
            return (
              part && textElements(splitText(part), style, variant, 'span')
            );
          }
          const ct = blankCounter ? blankCounter.next() : 0;
          return (
            <React.Fragment key={index}>
              {/* Inline blank rendered with underscores so it stays on the same
                  line and is visible in both PDF and Word (border-bottom on an
                  empty box is dropped in DOCX). */}
              <Box
                component="span"
                style={{ margin: '0 4px', whiteSpace: 'nowrap' }}
              >
                {'__________'}
              </Box>
              {part &&
                textElements(
                  splitText(part),
                  style,
                  variant || 'elementBodyText',
                  'span'
                )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  const renderElement = (element: ContentElementType) => {
    const elementType =
      element instanceof InstructionElementType
        ? element.instruction.case
        : element.model.case;
    const elementValue =
      element instanceof InstructionElementType
        ? element.instruction.value
        : element.model.value;
    const elem: { text?: string; style?: CSSProperties; variant?: any } = {};
    const BlankTextRegExp = /_{3,}/g;
    const tagName =
      elementType === 'teacherNote'
        ? 'NOTE'
        : elementType === 'ask' || elementType == 'askQuestion'
        ? 'QUESTION'
        : elementType === 'explain'
        ? 'EXPLANATION'
        : elementType?.toUpperCase();
    switch (elementType) {
      case 'text': {
        if (elementValue instanceof TextType) {
          elem.text = elementValue.text;
        }
      }
      /* falls through */
      case 'textCenter': {
        if (elementValue instanceof TextCenterType) {
          elem.text = elementValue.textCenter;
          elem.style = {
            textAlign: 'center',
            fontSize: '1em',
          };
        }
      }
      /* falls through */
      case 'textLeft': {
        if (elementValue instanceof TextLeftType) {
          elem.text = elementValue.textLeft;
          fontSize: '1em';
        }
      }
      /* falls through */
      case 'textRight': {
        if (elementValue instanceof TextRightType) {
          elem.text = elementValue.textRight;
          elem.style = {
            textAlign: 'right',
            fontSize: '1em',
          };
        }
      }
      /* falls through */
      case 'textBox': {
        if (elementValue instanceof TextBoxType) {
          elem.text = elementValue.boxText;
          elem.style = {
            fontSize: '1em',
          };
        }
      }
      /* falls through */
      case 'heading': {
        if (elementValue instanceof HeadingType) {
          elem.text = elementValue.heading;
          elem.style = {
            fontFamily: 'Poppins',
            color: '#1D1D1D',
            fontWeight: '800',
            fontSize: '1.4em',
          };
        }
      }
      /* falls through */
      case 'subHeading': {
        if (elementValue instanceof SubHeadingType) {
          elem.text = elementValue.subHeading;
          elem.style = {
            fontSize: '1em',
          };
        }
        const { text, style, variant } = elem;
        const qParts = text?.split(BlankTextRegExp) || [];
        if (isFibText) {
          return renderFibText(qParts, style, variant || 'elementBodyText');
        }
        return text ? (
          <Box
            style={
              elementType == 'textBox'
                ? styles.textBoxWrapper
                : styles.textElementsWrapper
            }
          >
            {textElements(splitText(text), style, variant || {})}
          </Box>
        ) : null;
      }
      case 'teacherImage':
      case 'image':
        if (elementValue instanceof ImageType) {
          const imageUrl = getMediaBasePath(
            elementValue.imageUrl,
            resourceCategory ===
              ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
              ? 'userDataBucket'
              : 'processedMediaBucket'
          );
          if (!showMedia) {
            return null;
          }
          return imageResizeEnabled ? (
            <ResizableImage url={imageUrl} alt={elementValue.imageName} />
          ) : (
            <ImageWithBase64 url={imageUrl} alt={elementValue.imageName} />
          );
        }
        return null;
      case 'video':
      case 'teacherVideo':
      case 'audio':
        return null;
      case 'numberList':
        if (elementValue instanceof NumberedListType) {
          return (
            <Box
              component="ol"
              style={{
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                paddingLeft: '16px',
                // 'li::marker' is not supported in style, so skip or use global CSS if needed
              }}
            >
              {elementValue.listItems.map((text, index) => {
                const qParts = text?.split(BlankTextRegExp) || [];
                if (isFibText) {
                  return renderFibText(qParts, {}, 'elementBodyText', 'li');
                }
                return (
                  <li
                    key={index}
                    style={{
                      marginBottom:
                        index === elementValue.listItems.length - 1
                          ? '0'
                          : '15px',
                    }}
                  >
                    {textElements(
                      splitText(text),
                      {},
                      'elementBodyText',
                      'span'
                    )}
                  </li>
                );
              })}
            </Box>
          );
        }
        return null;
      case 'bulletList':
        if (elementValue instanceof BulletedListType) {
          return (
            <Box
              component="ul"
              style={{
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                paddingLeft: '16px',
                // 'li::marker' is not supported in style, so skip or use global CSS if needed
              }}
            >
              {elementValue.listItems.map((text, index) => {
                const qParts = text?.split(BlankTextRegExp) || [];
                if (isFibText) {
                  return renderFibText(qParts, {}, 'elementBodyText', 'li');
                }
                return (
                  <li
                    key={index}
                    style={{
                      marginBottom:
                        index === elementValue.listItems.length - 1
                          ? '0'
                          : '15px',
                    }}
                  >
                    {textElements(
                      splitText(text),
                      {},
                      'elementBodyText',
                      'span'
                    )}
                  </li>
                );
              })}
            </Box>
          );
        }
        return null;
      case 'askQuestion':
        if (typeof elementValue == 'string') {
          return (
            <>
              {tagName && <ElementHeadingTag text={tagName} />}
              {textElements(
                splitText(elementValue),
                {},
                'elementBodyText',
                'span'
              )}
            </>
          );
        }
        return null;
      case 'askAnswer':
        if (typeof elementValue == 'string') {
          return (
            <>
              <PrintableText
                style={{
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                  fontSize: '1.17em',
                }}
              >
                Answer
              </PrintableText>
              {textElements(
                splitText(elementValue),
                {},
                'elementBodyText',
                'div'
              )}
            </>
          );
        }
        return null;
      case 'ask':
        if (elementValue instanceof AskInstructionModel) {
          return (
            <>
              {tagName && <ElementHeadingTag text={tagName} />}
              {textElements(
                splitText(elementValue.question),
                {},
                'elementBodyText',
                'span'
              )}
              <PrintableText
                style={{
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                  fontSize: '1.17em',
                }}
              >
                Answer
              </PrintableText>
              {textElements(
                splitText(elementValue.answer),
                {},
                'elementBodyText',
                'div'
              )}
            </>
          );
        }
        return null;
      case 'do':
      case 'teacherNote':
      case 'note':
      case 'explain':
      case 'say':
      case 'discuss':
        if (typeof elementValue == 'string') {
          return (
            <>
              {tagName && <ElementHeadingTag text={tagName} />}
              {textElements(
                splitText(elementValue),
                {},
                'elementBodyText',
                'span'
              )}
            </>
          );
        }
        return null;
      case 'comprehensionPassage':
        if (elementValue instanceof ComprehensionPassage) {
          return (
            <Box>
              {textElements(
                splitText(elementValue.comprehensionPassageText),
                {},
                'elementBodyText',
                'span'
              )}
            </Box>
          );
        }
        return null;
      case 'table':
        if (elementValue instanceof TableType && elementValue.table) {
          return (
            <TableElement
              resourceCategory={resourceCategory}
              table={elementValue.table}
            />
          );
        }
        return null;
      case 'excerpt':
        if (elementValue instanceof ExcerptType) {
          return (
            <Box>
              {textElements(
                splitText(elementValue.excerpt),
                {},
                'elementBodyText',
                'span'
              )}
            </Box>
          );
        }
        return null;
      default:
        return null;
    }
  };
  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {elements?.map((element, i) => {
        const elementCase =
          element instanceof InstructionElementType
            ? element.instruction.case
            : element.model?.case;
        // Keep image elements atomic even in splittable (PDF) mode so an image
        // is never sliced across a page boundary.
        const isImageElement =
          elementCase === 'image' || elementCase === 'teacherImage';
        const keepTogether = !splittable || isImageElement;
        return (
          <div
            style={{
              ...(keepTogether ? printableStyles : {}),
              marginBottom: '5px',
            }}
            // html2pdf pagebreak.avoid matches this class (inline CSS alone is not enough)
            className={keepTogether ? 'avoid-break-element' : undefined}
            key={i}
          >
            {renderElement(element)}
          </div>
        );
      })}
    </Box>
  );
};

// Helper function to fetch image as base64
export const fetchImageAsBase64 = async (
  url: string
): Promise<string | null> => {
  // console.log('fetching image as base64', url);
  try {
    const reqOptions: RequestInit = {};
    if (shouldIncludeCredentialsInRequest(url)) {
      reqOptions.credentials = 'include';
    }
    const response = await fetch(url, {
      ...reqOptions,
    });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to fetch image as base64:', error);
    return null;
  }
};

// Component to render image as base64
const ImageWithBase64: React.FC<{
  url: string;
  alt?: string;
  isSmallImage?: boolean;
}> = ({ url, alt, isSmallImage }) => {
  url = isGifMp4(url) ? url.replace('mp4', 'jpg') : url;
  const [base64, setBase64] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;
    if (url.startsWith('data:')) {
      setBase64(url);
    } else {
      fetchImageAsBase64(url).then((data) => {
        if (isMounted) setBase64(data);
      });
    }
    return () => {
      isMounted = false;
    };
  }, [url]);

  if (!base64) {
    return (
      <Box
        style={{
          width: '100%',
          minHeight: '100px',
          background: '#eee',
          ...printableStyles,
        }}
        className="avoid-break-element"
      />
    );
  }

  return (
    <Box
      component="img"
      src={base64}
      style={{
        maxHeight: isSmallImage ? '100px' : '250px',
        maxWidth: '100%',
        margin: '0 auto',
        ...printableStyles,
      }}
      className="avoid-break-element"
      alt={alt}
    />
  );
};

const resizableStyles = {
  wrapper: {
    display: 'inline-block',
    mx: 'auto',
    position: 'relative',
  } as const,
  resizeBox: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    cursor: 'pointer',
    padding: '7px',
    border: '1px solid #007CDC',
    backgroundColor: '#DBF0FF',
    borderRadius: '8px',
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    justifyContent: 'center',
  } as const,
};

const ResizableImage: React.FC<{ url: string; alt?: string }> = ({
  url,
  alt,
}) => {
  const [open, setOpen] = React.useState(false);
  return (
    <Box sx={resizableStyles.wrapper}>
      <ImageWithBase64 url={url} alt={alt} />
      <Box sx={resizableStyles.resizeBox} onClick={() => setOpen(true)}>
        <Box
          component="span"
          sx={{
            fontSize: 12,
            color: '#007CDC',
            display: { xs: 'none', md: 'inline' },
          }}
        >
          View Full Image
        </Box>
        <ImageWrapper
          name="resizeBox"
          type="png"
          parentFolder="icons"
          styles={{ height: '15px', width: '15px', display: 'block' }}
        />
      </Box>
      <ImagePopup
        imageName={alt}
        imageUrl={url}
        open={open}
        handleClose={() => setOpen(false)}
      />
    </Box>
  );
};

// PrintableText: renders parsed HTML using GeneoKatex. Renders a <span> when
// `component="span"` so inline content (e.g. fill-in-the-blanks parts) stays on
// the same line in both PDF and Word.
const PrintableText: React.FC<{
  children: React.ReactNode;
  style?: React.CSSProperties;
  component?: 'div' | 'span';
}> = ({ children, style, component = 'div' }) => {
  let text = '';
  if (children) {
    text = children.toString();
  }
  const Tag = component === 'span' ? 'span' : 'div';
  return (
    // Kept as an atomic (non-breakable) unit so a text line is never sliced
    // through the middle across a page boundary in PDF. Page breaks are allowed
    // between these paragraphs via the splittable wrappers instead.
    <Tag
      style={{
        // The global `.avoid-break-element` class forces `display: block`; an
        // inline style is needed to keep span variants actually inline in PDF.
        ...(component === 'span' ? { display: 'inline' } : {}),
        ...style,
        ...printableStyles,
      }}
      className="avoid-break-element"
      dangerouslySetInnerHTML={{ __html: new GeneoKatex(text).parsedHtml() }}
    />
  );
};

/** Whole number when exact; otherwise one decimal place (e.g. 1, 3.3). */
const formatMarksDisplay = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

interface PrintableQuestionProps {
  ques?: Question;
  number?: number | string;
  marks?: number;
  showAnswer?: boolean;
  isAiGenerated?: boolean;
  showMedia?: boolean;
  hideOptions?: boolean;
  hideQuestionNumber?: boolean;
  imageResizeEnabled?: boolean;
  /**
   * Render the question layout using HTML tables instead of flexbox.
   * Word (.docx) does not support flexbox/gap, so table layout keeps the
   * question number, body and marks on the same line when exported to DOCX.
   */
  useTableLayout?: boolean;
  /**
   * Allow long question text to break across pages (used for PDF export) instead
   * of keeping the whole question together. Images stay non-breakable.
   */
  splittable?: boolean;
  /**
   * When set, render only the Q number column + this label (no question body).
   * Used for grouped-question headers so Q.n aligns with other questions.
   */
  headerOnlyText?: string;
}

const PrintableQuestion: React.FC<PrintableQuestionProps> = ({
  ques,
  number,
  marks,
  showAnswer,
  isAiGenerated,
  showMedia,
  hideOptions,
  hideQuestionNumber,
  imageResizeEnabled,
  useTableLayout,
  splittable,
  headerOnlyText,
}) => {
  const marksLabel =
    typeof marks === 'number' && marks > 0
      ? `[${formatMarksDisplay(marks)}]`
      : null;

  if (headerOnlyText) {
    const headerBody = (
      <PrintableText style={{ fontSize: '1em', fontWeight: 'bold' }}>
        {headerOnlyText}
      </PrintableText>
    );
    if (useTableLayout) {
      return (
        <table
          border={0}
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: '5px',
            ...printableStyles,
          }}
          className="avoid-break-element"
        >
          <tbody>
            <tr>
              {!hideQuestionNumber && (
                <td
                  style={{
                    width: '34px',
                    verticalAlign: 'top',
                    textAlign: 'left',
                    paddingRight: '6px',
                  }}
                >
                  <PrintableText style={{ fontSize: '1em' }}>
                    {number ? 'Q.' + number : null}
                  </PrintableText>
                </td>
              )}
              <td style={{ verticalAlign: 'top' }}>{headerBody}</td>
              <td
                style={{
                  width: '44px',
                  verticalAlign: 'top',
                  textAlign: 'right',
                  paddingLeft: '6px',
                }}
              >
                <PrintableText style={{ fontSize: '1em' }}>
                  {marksLabel}
                </PrintableText>
              </td>
            </tr>
          </tbody>
        </table>
      );
    }
    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          marginBottom: '5px',
        }}
      >
        {!hideQuestionNumber && (
          <Box
            style={{
              ...printableStyles,
              paddingRight: '10px',
              minWidth: '30px',
              textAlign: 'right',
            }}
            className="avoid-break-element"
          >
            <PrintableText style={{ fontSize: '1em' }}>
              {number ? 'Q.' + number : null}
            </PrintableText>
          </Box>
        )}
        <Box style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {headerBody}
        </Box>
        <Box
          style={{
            ...printableStyles,
            marginLeft: 'auto',
            paddingLeft: '10px',
            paddingRight: '10px',
          }}
          className="avoid-break-element"
        >
          <PrintableText style={{ fontSize: '1em' }}>
            {marksLabel}
          </PrintableText>
        </Box>
      </Box>
    );
  }

  const question = new QuestionContentModel(ques?.question);
  const elements = question?.model.value?.commonQuestionContent?.elements;
  const options =
    question?.model.value instanceof McqSingleContentModel ||
    question?.model.value instanceof McqMultipleContentModel
      ? question?.model.value.options
      : [];
  const isFibText = question?.model.case === 'fibContentModel';
  const isTrueFalse = question?.model.case === 'tfContentModel';
  const solution = question.model.value?.commonQuestionContent?.solution || [];
  const solutionWithoutVideo = solution.filter((e) => e.model.case !== 'video');
  let solutionText = getSolutionText(ques, undefined, true);
  solutionText = typeof solutionText === 'string' ? solutionText.trim() : '';

  // Options for PDF flex path. Avoid display:flex on the avoid-break wrapper —
  // html2pdf ignores page-break-inside:avoid on flex containers, which mid-slices
  // option lines across pages. Block + single text node keeps each option atomic.
  const renderOptionsFlex = () => (
    <Box>
      {options.map((option, index) => {
        const opt = option.optionType.value;
        if (opt instanceof TextOption) {
          return (
            <div
              style={{ marginBottom: '5px', ...printableStyles }}
              className="avoid-break-element"
              key={'option-' + index}
            >
              <PrintableText>
                {numberToColumnTitle(index) + ') ' + (opt?.optionText ?? '')}
              </PrintableText>
            </div>
          );
        }
        if (opt instanceof ImageOption) {
          const imageUrl = getMediaBasePath(
            opt.image?.imageUrl,
            'processedMediaBucket'
          );
          return (
            <div
              key={index}
              style={{ marginBottom: '5px', ...printableStyles }}
              className="avoid-break-element"
            >
              <PrintableText>{numberToColumnTitle(index)})</PrintableText>
              <ImageWithBase64
                url={imageUrl}
                alt={opt?.image?.imageName}
                isSmallImage={true}
              />
            </div>
          );
        }
        return null;
      })}
    </Box>
  );

  // Options rendered without flexbox so Word keeps the option label and the
  // option content on the same line. Text options concatenate the label into a
  // single paragraph. Image options render the label then the image as separate
  // blocks (Word / html-to-docx cannot nest a table inside a cell, and the whole
  // question is a table in this mode).
  const renderOptionsTable = () => (
    <Box>
      {options.map((option, index) => {
        const opt = option.optionType.value;
        if (opt instanceof TextOption) {
          return (
            <div
              style={{ marginBottom: '5px', ...printableStyles }}
              className="avoid-break-element"
              key={'option-' + index}
            >
              <PrintableText>
                {numberToColumnTitle(index) + ') ' + (opt?.optionText ?? '')}
              </PrintableText>
            </div>
          );
        }
        if (opt instanceof ImageOption) {
          const imageUrl = getMediaBasePath(
            opt.image?.imageUrl,
            'processedMediaBucket'
          );
          return (
            <div
              key={index}
              style={{ marginBottom: '5px' }}
              className="avoid-break-element"
            >
              <PrintableText>{numberToColumnTitle(index)})</PrintableText>
              <ImageWithBase64
                url={imageUrl}
                alt={opt?.image?.imageName}
                isSmallImage={true}
              />
            </div>
          );
        }
        return null;
      })}
    </Box>
  );

  const questionBody = (
    <>
      <Box style={{ marginBottom: '5px' }}>
        {renderElements(
          elements,
          isFibText,
          undefined,
          showMedia,
          imageResizeEnabled,
          splittable
        )}
      </Box>
      {!hideOptions && isTrueFalse && (
        <Box style={{ marginBottom: '5px' }}>
          <PrintableText>(True/False)</PrintableText>
        </Box>
      )}
      {!hideOptions &&
        !isTrueFalse &&
        options.length > 0 &&
        (useTableLayout ? renderOptionsTable() : renderOptionsFlex())}
      {showAnswer && solutionText && (
        <Box style={{ marginBottom: '5px' }}>
          <PrintableText>
            {'Answer: ' + getSolutionText(ques, undefined, true)}
          </PrintableText>
        </Box>
      )}
      {showAnswer && solutionWithoutVideo.length > 0 && (
        <Box sx={{ marginBottom: '5px' }}>
          <PrintableText>{'Solution: '}</PrintableText>
          {renderElements(
            solutionWithoutVideo,
            isFibText,
            undefined,
            showMedia,
            imageResizeEnabled,
            splittable
          )}
        </Box>
      )}
    </>
  );

  // Table layout: keeps question number, body and marks on the same line in
  // Word (which does not support flexbox / gap).
  if (useTableLayout) {
    return (
      <table
        border={0}
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: '5px',
          ...printableStyles,
        }}
        className="avoid-break-element"
      >
        <tbody>
          <tr>
            {!hideQuestionNumber && (
              <td
                style={{
                  width: '34px',
                  verticalAlign: 'top',
                  textAlign: 'left',
                  paddingRight: '6px',
                }}
              >
                <PrintableText style={{ fontSize: '1em' }}>
                  {number ? 'Q.' + number : null}
                </PrintableText>
              </td>
            )}
            <td style={{ verticalAlign: 'top' }}>{questionBody}</td>
            <td
              style={{
                width: '44px',
                verticalAlign: 'top',
                textAlign: 'right',
                paddingLeft: '6px',
              }}
            >
              <PrintableText style={{ fontSize: '1em' }}>
                {marksLabel}
              </PrintableText>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  // When not splittable, prefer a block avoid-break root over flex — html2pdf
  // reliably honors page-break avoid on block/table, not on flex containers.
  if (splittable === false) {
    return (
      <div
        style={{
          display: 'table',
          width: '100%',
          marginBottom: '5px',
          ...printableStyles,
        }}
        className="avoid-break-element"
      >
        <div style={{ display: 'table-row' }}>
          {!hideQuestionNumber && (
            <div
              style={{
                display: 'table-cell',
                verticalAlign: 'top',
                paddingRight: '10px',
                width: '34px',
                textAlign: 'right',
              }}
            >
              <PrintableText style={{ fontSize: '1em' }}>
                {number ? 'Q.' + number : null}
              </PrintableText>
            </div>
          )}
          <div style={{ display: 'table-cell', verticalAlign: 'top' }}>
            {questionBody}
          </div>
          <div
            style={{
              display: 'table-cell',
              verticalAlign: 'top',
              paddingLeft: '10px',
              paddingRight: '10px',
              width: '44px',
              textAlign: 'right',
            }}
          >
            <PrintableText style={{ fontSize: '1em' }}>
              {marksLabel}
            </PrintableText>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: '5px',
      }}
    >
      {!hideQuestionNumber && (
        <Box
          style={{
            ...printableStyles,
            paddingRight: '10px',
            minWidth: '30px',
            textAlign: 'right',
          }}
          className="avoid-break-element"
        >
          <PrintableText style={{ fontSize: '1em' }}>
            {number ? 'Q.' + number : null}
          </PrintableText>
        </Box>
      )}
      <Box style={{ display: 'flex', flexDirection: 'column' }}>
        {questionBody}
      </Box>
      <Box
        style={{
          ...printableStyles,
          marginLeft: 'auto',
          paddingLeft: '10px',
          paddingRight: '10px',
        }}
        className="avoid-break-element"
      >
        <PrintableText style={{ fontSize: '1em' }}>
          {marksLabel}
        </PrintableText>
      </Box>
    </Box>
  );
};

export default PrintableQuestion;
